import { readFileSync } from 'node:fs';
import { dirname, resolve } from 'node:path';
import { fileURLToPath, pathToFileURL } from 'node:url';
import { TraceMap, originalPositionFor } from '@jridgewell/trace-mapping';
import { isBunRuntime } from '@gitlab-org/core';

// null = no source map available for this file (negative result is cached too).
const traceMaps = new Map<string, TraceMap | null>();

/**
 * Rewrites Error stack traces to original source positions using each file's
 * source map, via a custom `Error.prepareStackTrace`.
 *
 * A custom hook is used rather than the `source-map-support` package: its eager
 * VLQ decoder blocks the event loop for ~1s on the first stack access against
 * our ~30MB bundle map, long enough to time out concurrent in-flight connection
 * attempts. `@jridgewell/trace-mapping` resolves the same lookup in ~100ms, but
 * is lookup-only, so needs this manual hook.
 */
export function installSourceMapStacks(): void {
  // Bun natively source-maps stack traces (inline map embedded at compile time),
  // and its frame paths are not readable from disk; installing the hook there
  // would replace correct native mapping with unmapped fallback frames.
  if (isBunRuntime()) {
    return;
  }
  Error.prepareStackTrace = prepareStackTrace;
}

// Anything thrown here escapes the `.stack` getter and would turn an error log into a
// crash, so every step stays inside a guard.
export function prepareStackTrace(error: Error, callSites: NodeJS.CallSite[]): string {
  let header: string;
  try {
    header = String(error);
  } catch {
    header = '<error>';
  }
  let frames: string[];
  try {
    frames = callSites.map((callSite) => `\n    at ${formatCallSite(callSite)}`);
  } catch {
    frames = [];
  }
  return `${header}${frames.join('')}`;
}

function formatCallSite(callSite: NodeJS.CallSite): string {
  let generated: string;
  try {
    generated = callSite.toString();
  } catch {
    return '<unknown>';
  }
  try {
    const fileName = callSite.getFileName();
    const line = callSite.getLineNumber();
    const column = callSite.getColumnNumber();
    if (!fileName || line === null || column === null) {
      return generated;
    }
    const map = getTraceMap(fileName);
    if (!map) {
      return generated;
    }
    // CallSite columns are 1-based; trace-mapping columns are 0-based.
    const position = originalPositionFor(map, { line, column: column - 1 });
    if (position.source === null || position.line === null) {
      return generated;
    }
    const source = position.source.startsWith('file://')
      ? fileURLToPath(position.source)
      : position.source;
    // Replacement must stay a function: `source` comes from the source map, and as a
    // string it would have `$&` / `$1` / `$'` interpreted as replacement patterns.
    return generated.replace(
      `${fileName}:${line}:${column}`,
      () => `${source}:${position.line}:${(position.column ?? 0) + 1}`,
    );
  } catch {
    return generated;
  }
}

function getTraceMap(fileName: string): TraceMap | null {
  let map = traceMaps.get(fileName);
  if (map === undefined) {
    map = loadTraceMap(fileName);
    traceMaps.set(fileName, map);
  }
  return map;
}

function loadTraceMap(fileName: string): TraceMap | null {
  try {
    const fsPath = fileName.startsWith('file://') ? fileURLToPath(fileName) : fileName;
    const source = readFileSync(fsPath, 'utf8');
    const pattern = /\/\/[#@][ \t]*sourceMappingURL=([^\s'"]+)[ \t]*$/gm;
    let match = pattern.exec(source);
    let last: RegExpExecArray | null = null;
    while (match !== null) {
      last = match;
      match = pattern.exec(source);
    }
    if (!last) {
      return null;
    }
    const url = last[1];
    if (url.startsWith('data:')) {
      const base64Marker = 'base64,';
      const base64Index = url.indexOf(base64Marker);
      if (base64Index === -1) {
        return null;
      }
      const base64 = url.slice(base64Index + base64Marker.length);
      const json = Buffer.from(base64, 'base64').toString('utf8');
      return new TraceMap(JSON.parse(json), fileName);
    }
    const mapPath = resolve(dirname(fsPath), url);
    return new TraceMap(
      JSON.parse(readFileSync(mapPath, 'utf8')),
      pathToFileURL(mapPath).toString(),
    );
  } catch {
    return null;
  }
}
